import emptySnail from 'assets/image/snail-no.png'
import clsx from 'clsx'
import { useSettings } from 'features/messaging/hooks/useSettings'
import { useRef } from 'react'
import typo from 'shared/styles/typography.module.scss'

import { api } from '../../api'
import { useAutoSync, useCurrentId, useReactOnSyncChange } from '../../hooks'
import { useSelectFirstChat } from '../../hooks'
import { useGetProps } from '../../hooks/useFilters'
import { Thumbnail, ThumbnailSkeleton } from '../Thumbnail'
import css from './Conversations.module.css'
import { NextPack } from './NextPack'
import { ProxyUnavailableError } from '../../../../shared/errors'

function mailboxEmptyStateFigure(text: string) {
	return (
		<figure className={clsx(css.empty, typo.buttonSmB)}>
			<img
				alt='Snail show stop sign'
				height={209}
				src={emptySnail}
				width={120}
			/>
			<figcaption>{text}</figcaption>
		</figure>
	)
}

export function Conversations() {
	const { filter, mailboxFilter, search } = useGetProps()
	const mailboxRef = useRef<HTMLUListElement | null>(null)

	const current = useCurrentId()
	const pullingOptions = useAutoSync()

	const settingQuery = useSettings()

	const query = api.useConversationsQuery(
		{ cursor: '', filter, mailboxFilter, search },
		{
			...pullingOptions,
			refetchOnMountOrArgChange: true
		}
	)

	useReactOnSyncChange(settingQuery.isSuccess && query.refetch)
	useSelectFirstChat(query.currentData?.items[0])

	const showLoading =
		query.isLoading || (query.isFetching && !query.currentData)

	const isError = Boolean(query.isError && !query.currentData)

	if (isError || query.currentData?.items.length === 0) {
		const emptyMessage = query.error instanceof ProxyUnavailableError
			? query.error.message
			: 'No results found'
		const result = mailboxEmptyStateFigure(emptyMessage)
		return result
	}

	const hasNextCursor = Boolean(
		query.isSuccess && query.currentData?.nextCursor && mailboxRef.current
	)

	return (
		<ul className={`app-scroll ${css.chats}`} id='mailbox' ref={mailboxRef}>
			{(showLoading || query.isUninitialized) &&
				Array.from({ length: 14 }, (_, i) => <ThumbnailSkeleton key={i} />)}
			{!showLoading &&
				query.currentData?.items.map(conversation => (
					<Thumbnail
						id={conversation.entity_id}
						isSelected={conversation.entity_id === current}
						key={conversation.entity_id}
					/>
				))}

			{hasNextCursor && (
				<NextPack
					cursor={query.currentData!.nextCursor!}
					rootRef={mailboxRef}
				/>
			)}
		</ul>
	)
}
